import type { UIMessage } from 'ai';
import { createContext, useContext, type ReactNode } from 'react';

export interface ICommendaAiHostOrganization {
  id: string;
  name: string;
}

/** Everything this tree needs from the app it is mounted in. The client app reads it from its own
 * config context and router; an app with no equivalent (the admin console) supplies what it has and
 * omits the rest, which is why every optional member degrades to rendering nothing. */
export interface ICommendaAiHost {
  /** The workspace a chat is scoped to when no {@link CommendaAiWorkspaceContext} override is
   * mounted. Null is a real value: a firm-scope surface has no single company. */
  companyId: number | null;
  /** True while the host is still resolving `companyId`, so a request is not fired against null. */
  isWorkspaceLoading: boolean;
  organizations: readonly ICommendaAiHostOrganization[];
  user: { id: string; name?: string | null } | null;
  /** Routes the app to an in-app path. Pages Router and App Router disagree on which `useRouter`
   * is legal, so the host supplies the push rather than the tree importing one. */
  navigate: (path: string) => void;
  /** Routes to a record the agent named, switching the app into that record's workspace first.
   * Absent falls back to {@link navigate}. */
  openRecord?: (target: { path: string; companyId: number | null }) => void;
  /** The app's OAuth connector card, rendered above the composer when the last turn asked the
   * reader to authorize an app. Absent renders nothing. */
  renderConnectorPrompt?: (messages: UIMessage[] | null) => ReactNode;
  /** The app's connector directory. Absent also removes the composer menu entry that opens it, so
   * a surface without connector UI cannot offer a control that does nothing. */
  renderConnectorsModal?: (props: {
    isOpen: boolean;
    onClose: () => void;
  }) => ReactNode;
}

const NO_HOST: ICommendaAiHost = {
  companyId: null,
  isWorkspaceLoading: false,
  organizations: [],
  user: null,
  navigate: () => undefined,
};

export const CommendaAiHostContext = createContext<ICommendaAiHost | undefined>(
  undefined
);

/** The host bindings, or an inert set when no provider is mounted, so the tree still renders
 * read-only in a preview or a story. */
export const useCommendaAiHost = (): ICommendaAiHost =>
  useContext(CommendaAiHostContext) ?? NO_HOST;

interface CommendaAiHostProviderProps {
  host: ICommendaAiHost;
  children: ReactNode;
}

/** Mounted once per app, above every Commenda AI surface. */
export const CommendaAiHostProvider = ({
  host,
  children,
}: CommendaAiHostProviderProps) => (
  <CommendaAiHostContext.Provider value={host}>
    {children}
  </CommendaAiHostContext.Provider>
);
